/** @jsx jsx */
import { Inline, jsx, Stack, Text } from '@front10/base-elements';
import { IconName } from '../../../packages/base-elements/Icon/Icon';
import { useTranslation } from '@front10/utils';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Dates } from '../../lab/DateRangePicker';
import { Listbox } from '../../lab/Listbox';
import { getPeopleObject, PeopleInfo, useDefaultPeopleSelection } from '../../lab/PeopleCounter';
import { SelectConfigurationExtended, SelectProps, useSelectProps } from '../../lab/utils';
import { AdvancedFilterSelector, getDefaultRules } from './AdvancedFilterSelector';
import { FetchAirlines } from './airlines';
import { Airport, AirportMarketFilter, FetchAirports } from './airports';
import { PassengersProps, RoomsProps } from './bookingBusinessRules';
import { BookingComponentsConfigurations } from './bookingComponentsConfigurations';
import { BookingFilterLabels, BookingFilters } from './BookingFilters';
import { OriginDestinationLogic } from '../_commons/filters';
import { PassengerInfoSelector } from './PassengerInfoSelector';
import { buildRoom, RoomInfo, RoomsSelector } from './RoomsSelector';
import { Rule } from './Rules';
import React from 'react';
import { FetchBlackoutDates, useBlackoutDates } from './blackoutDates';
import Alert from '../../lab/Alert';
import { ListSelector } from '../../lab/ListSelector';

export type PanelSingleType =
  | 'flight'
  | 'hotel'
  | 'car'
  | 'activity'
  | 'flight+hotel'
  | 'flight+car+hotel'
  | 'flight+car';

interface PanelComplexType extends SelectProps {
  defaultValue: PanelSingleType;
}

export interface BookingPanelProps {
  name: string;
  label: string;
  icon: IconName;
  type: PanelSingleType | PanelComplexType;
  labels: BookingFilterLabels;
  blackoutDates?: {
    minDate: number;
    maxDate: number;
  };
  maxTripDuration?: number;
  minTripDuration?: number;
  advancedSelector: {
    enabledBottomBtn: boolean;
  };
  passengerSelector: {
    enabledBottomBtn: boolean;
  };
  enableLegend?: boolean;
  enableAirlineFilter?: boolean;
}

interface Props {
  onSubmit: (data: any) => void;
  onOriginChange?: (state: BookingPanelState) => void;
  onAirlineChange?: (state: BookingPanelState) => void;
  onDestinationChange?: (state: BookingPanelState) => void;
  onJourneyTypeChange?: (state: BookingPanelState) => void;
  onDepartureDateChange?: (state: BookingPanelState) => void;
  onReturnDateChange?: (state: BookingPanelState) => void;
  onCurrentPopupDataChange?: (state: BookingPanelState) => void;
  layout?: 'fluid' | 'side-banner' | 'dialog';
  fetchAirports: FetchAirports;
  fetchAirlines: FetchAirlines;
  fetchBlackoutDates: FetchBlackoutDates;
  filterFields?: AirportMarketFilter;
  journeyTypes: SelectProps;
  hotelCategories: SelectProps;
  travelClasses: SelectConfigurationExtended;
  ages: SelectProps;
  passengers: PassengersProps;
  passengersRules?: Array<string>;
  dropOff: SelectProps;
  nationalities: SelectProps;
  additionalRules?: Array<Rule>;
  rooms?: RoomsProps;
  carTypes: SelectProps;
  defaultOrigin?: string;
  defaultDestination?: string;
  prepopulatedAirlines?: string;
  defaultDates?: Dates;
  type: BookingPanelProps['type'];
  labels: BookingFilterLabels;
  componentsConfigurations: BookingComponentsConfigurations;
  overrideComponentsConfigurations?: BookingComponentsConfigurations;
  blackoutDates?: {
    minDate: number;
    maxDate: number;
  };
  maxTripDuration?: number;
  minTripDuration?: number;
  airlines?: {
    airlinesList: Array<{}>;
    enabled: boolean;
  };
  passengerSelector?: {
    enabledBottomBtn: boolean;
  };
  advancedSelector?: {
    enabledBottomBtn: boolean;
  };
  showAdvancedFilter?: boolean;
  activeTab?: number;
  currentTab?: number;
  promoCode?: string;
  originDestinationLogic?: string;
  businessRules?: any;
  excludeBasicEconomy?: boolean;
  disclaimer?: any;
  isMobile?: boolean;
  preventBottomDisplacement?: boolean;
  allowHoverStyleInRange?: boolean;
  enableLegend?: boolean;
}

export interface BookingPanelState {
  airline?: string;
  type: PanelSingleType;
  flightType?: string;
  hotelCategory?: string;
  hotelId?: string;
  travelClass: string;
  passengerInfo?: PeopleInfo;
  passengers?: Record<string, number>;
  origin?: Airport;
  destination?: Airport;
  dates?: Dates;
  rooms?: RoomInfo[];
  carType?: string;
  carCompany?: string;
  hotel?: string;
  rules?: Record<string, boolean>;
  dropOff?: string;
  promoCode?: string;
  excludeBasicEconomy?: boolean;
  airlines?: {
    label: string;
    value: string;
  };
}

interface OptionsType {
  value: string;
  label: string;
  href?: string | undefined;
  rules?: {}[] | undefined;
}

const defaultRules: Rule[] = [];
const ENTER_KEY = 13;

export function BookingPanel(props: Props) {
  const defaultType = typeof props.type === 'string' ? props.type : props.type.defaultValue;
  const defaultRooms = useDefaultPeopleSelection(
    props.rooms?.options ?? [],
    props.rooms?.defaultSelection ?? {}
  );

  const rules = props.additionalRules ?? defaultRules;
  const [defaultValues] = useState(() => ({
    type: defaultType,
    flightType: props.journeyTypes.defaultValue,
    hotelCategory: props.hotelCategories.defaultValue,
    travelClass: props.travelClasses.defaultValue,
    passengers: props.passengers.defaultSelection,
    origin: { value: props.defaultOrigin ?? '', label: '' },
    destination: { value: props.defaultDestination ?? '', label: '' },
    dates: props.defaultDates,
    rooms: props.rooms ? [buildRoom(props.rooms.options.length, defaultRooms)] : undefined,
    carType: props.carTypes.defaultValue,
    rules: getDefaultRules(rules),
    dropOff: props.dropOff.defaultValue,
    age: props.ages.defaultValue,
    nationality: props.nationalities.defaultValue
  }));

  const {
    handleSubmit,
    watch,
    control,
    errors,
    getValues,
    setValue,
    register,
    trigger
  } = useForm<BookingPanelState>({
    defaultValues
  });
  const type = watch('type', defaultType) as PanelSingleType;
  const travelClass = watch('travelClass', defaultValues.travelClass);
  const dropOff = watch('dropOff', defaultValues.dropOff);
  const carType = watch('carType', defaultValues.carType);
  const flightType = watch('flightType');
  const rooms = watch('rooms');
  const [selectedOrigin, setSelectedOrigin] = useState(props.defaultOrigin ?? '');
  const [selectedDestination, setSelectedDestination] = useState(props.defaultDestination ?? '');
  const [selectedDepartureDate, setSelectedDepartureDate] = useState<Date | undefined>(
    props.defaultDates?.start
  );
  const [selectedReturnDate, setSelectedReturnDate] = useState<Date | undefined>(
    props.defaultDates?.end
  );

  const [loading, setLoading] = useState(false);

  // Get the loading state when sumbit is called
  const handleInternalSubmit = () => {
    setLoading(true);
    // Simulate an async operation like API call
    setTimeout(() => {
      setLoading(false);
    }, 10000);
  };
  // Reset the loading state when errors are handled
  const handleErrors = () => {
    setLoading(false); // Ensure loading is not set if there are errors
  };

  const isInternational = React.useMemo(() => {
    return (
      getValues()?.origin?.country &&
      getValues()?.destination?.country &&
      getValues()?.origin?.country?.isoCode !== getValues()?.destination?.country?.isoCode
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues().origin?.country, getValues().destination?.country]);

  const travelClasses = useSelectProps(
    props.travelClasses.activateRouteOption
      ? isInternational
        ? props.travelClasses?.international ?? props.travelClasses
        : props.travelClasses?.domestic ?? props.travelClasses
      : props.travelClasses
  );

  useEffect(() => {
    if (props.travelClasses.activateRouteOption) {
      setValue('travelClass', travelClasses.defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelClasses.defaultValue]);

  const compactLayout = props.layout === 'side-banner' || props.layout === 'dialog';
  const translate = useTranslation();
  const config = useMemo(
    () => ({
      ...props.componentsConfigurations,
      ...props.overrideComponentsConfigurations
    }),
    [props.componentsConfigurations, props.overrideComponentsConfigurations]
  );

  let columns;
  let rows;

  if (compactLayout) {
    columns = [[1]];
    if (type === 'flight') {
      rows = ['60px', '60px', '60px', 'minmax(0px, 1fr)'];
    } else if (type === 'hotel' || type === 'activity') {
      rows = ['auto', 'auto', 'minmax(0px, 1fr)'];
    } else if (type === 'car') {
      rows = ['auto', '150px', 'minmax(0px, 1fr)'];
    } else {
      rows = ['auto', 'auto', 'auto', 'minmax(0px, 1fr)'];
    }
  } else if (type === 'car') {
    columns = [[1], [1], [1], dropOff === 'different' ? [1, 1, 3, 1] : [2, 3, 1]];
  } else if (type === 'hotel' || type === 'activity') {
    columns = [[1], [1], [1], [2, 2, 1]];
  } else {
    columns = [
      [1],
      [1],
      [1, 1],
      props.componentsConfigurations.airlinesSelector?.enabled ? [1, 1, 2, 1, 1] : [2, 2, 3, 1.55]
    ];
  }

  useEffect(() => {
    props.onCurrentPopupDataChange?.({ ...getValues() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getValues,
    flightType,
    selectedOrigin,
    selectedDestination,
    selectedDepartureDate,
    selectedReturnDate,
    travelClass,
    rooms
  ]);

  const isAllowfetchBlackoutDates = (origin = '', destination = '', isRoundtrip = '') => {
    return Boolean(
      origin &&
        origin !== '' &&
        destination &&
        destination !== '' &&
        Boolean(config?.blackoutDatesIntegration) &&
        isRoundtrip !== undefined
    );
  };

  const fetchBlackoutDates = props.fetchBlackoutDates;
  const { data: blackoutDates = null } = useBlackoutDates(props.fetchBlackoutDates, {
    selectedOrigin: Boolean(config?.blackoutDatesIntegration) ? getValues()?.origin?.value : '',
    selectedDestination: Boolean(config?.blackoutDatesIntegration)
      ? getValues()?.destination?.value
      : '',
    isRoundtrip: getValues()?.flightType === 'rt',
    makeQuery: isAllowfetchBlackoutDates(
      getValues()?.origin?.value,
      getValues()?.destination?.value,
      flightType
    ),
    selectedDepartureDate: getValues()?.dates?.start,
    selectedReturnDate: getValues()?.dates?.end
  });

  const onOriginChange = props.onOriginChange;
  const handleOriginChange = useCallback(
    (origin: Airport) => {
      onOriginChange?.({ ...(getValues() as BookingPanelState), origin }); // TODO: Remove type assertion

      if (type === 'car' && dropOff === 'same') {
        setValue('destination', origin);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues, onOriginChange, fetchBlackoutDates]
  );

  const onAirlineChange = props.onAirlineChange;
  const handleOnAirlineChange = useCallback(
    (airlines: any) => {
      onAirlineChange?.({ ...getValues(), airlines });
    },
    [getValues, onAirlineChange]
  );

  const onDestinationChange = props.onDestinationChange;
  const handleDestinationChange = useCallback(
    (destination: Airport) => {
      onDestinationChange?.({ ...(getValues() as BookingPanelState), destination }); // TODO: Remove type assertion
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues, onDestinationChange]
  );

  const onJourneyTypeChange = props.onJourneyTypeChange;
  const handleJourneyTypeChange = useCallback(
    (flightType: any) => {
      onJourneyTypeChange?.({ ...getValues(), flightType });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues, onJourneyTypeChange]
  );

  const onDepartureDateChange = props.onDepartureDateChange;
  const handleDepartureDateChange = useCallback(
    (departureDate: Date | undefined, returnDate: Date | undefined) => {
      let values = { ...(getValues() as BookingPanelState) };
      if (values.dates !== undefined) {
        values.dates.start = departureDate;
        values.dates.end = returnDate;
      }
      onDepartureDateChange?.(values);
    },
    [getValues, onDepartureDateChange]
  );

  const onReturnDateChange = props.onReturnDateChange;
  const handleReturnDateChange = useCallback(
    (returnDate: Date | undefined, departureDate: Date | undefined) => {
      let values = { ...(getValues() as BookingPanelState) };
      if (values.dates !== undefined) {
        values.dates.end = returnDate;
        values.dates.start = departureDate;
      }
      onReturnDateChange?.(values);
    },
    [getValues, onReturnDateChange]
  );

  function getOptions(options: Array<OptionsType>) {
    return options.map((val: OptionsType) => ({
      value: val.value,
      label: translate(val.label, val.label),
      href: val.href,
      rules: val.rules
    }));
  }

  const checkKeyDown = (e: any) => {
    if (
      e.keyCode === ENTER_KEY &&
      e.target.getAttribute('type') !== 'submit' &&
      e.target.getAttribute('type') !== 'button'
    ) {
      e.preventDefault();
    }
  };

  return (
    <form
      onSubmit={(e: any) => {
        e.preventDefault();
      }}
      onKeyDown={(e) => checkKeyDown(e)}
      style={props.isMobile ? { height: '100%' } : undefined}
    >
      <input type="hidden" name="travelClass" ref={register} />
      <Controller name="passengers" control={control} defaultValue={defaultValues.passengers} />
      <Controller name="rooms" control={control} defaultValue={defaultValues.rooms} />

      <Stack
        gap={type !== 'activity' ? '15px' : 0}
        style={props.isMobile ? { height: '100%' } : undefined}
      >
        {props?.enableLegend ? (
          <Alert
            icon="info"
            labelKey={props.labels.legend}
            sx={{
              padding: '8px',
              backgroundColor: '#eeeeee',
              color: 'softText',
              variant: 'layout.legend'
            }}
          />
        ) : null}

        {type === 'activity' ? (
          <Inline className="booking-activity-message-container" style={{ marginBottom: 15 }}>
            <Text variant="text.fontSize5" className="booking-activity-message">
              {translate('cmp-booking-activity-message', 'Find tickets, tours and activities')}
            </Text>
          </Inline>
        ) : null}
        <Inline gap="5px" childrenBottomMargin wrap={true}>
          <Controller
            as={
              typeof props.type !== 'string' ? (
                <Listbox
                  label={translate('cmp-filters-package-type', 'Package type')}
                  options={getOptions(props.type.options)}
                  defaultValue={type}
                />
              ) : undefined
            }
            defaultValue={defaultType}
            name="type"
            control={control}
          />
          <Controller
            render={({ onChange }) =>
              config.journeyTypeSelector?.enabled !== false &&
              type !== 'hotel' &&
              type !== 'car' &&
              type !== 'activity' ? (
                <Listbox
                  label={translate('cmp-filters-journey-type', 'Journey type')}
                  options={props.journeyTypes.options}
                  defaultValue={flightType}
                  focusWhenIsShow={false}
                  onChange={(newValue) => {
                    onChange(newValue);
                    handleJourneyTypeChange(newValue);
                  }}
                />
              ) : (
                <React.Fragment></React.Fragment>
              )
            }
            name="flightType"
            control={control}
          />
          {type === 'flight+car' || type === 'flight' ? (
            <PassengerInfoSelector
              key={travelClasses.defaultValue}
              origin={selectedOrigin}
              destination={selectedDestination}
              travelClasses={travelClasses}
              activateRouteOption={props.travelClasses.activateRouteOption}
              passengers={props.passengers}
              passengersRules={props.passengersRules}
              travelClassConfig={config.travelClassSelector}
              onChange={(value) => {
                const [passengers, ages] = getPeopleObject(
                  props.passengers.options,
                  value.passengers
                );
                setValue('passengers', passengers as Record<string, number>);
                setValue('ages', ages);
                setValue('travelClass', value.travelClass);
                const enableShowDetailedAgeOptions =
                  props.businessRules.travelers.options.findIndex(
                    (e: any) => e.showDetailedAgeOptions
                  ) !== -1;
                if (enableShowDetailedAgeOptions) {
                  const val =
                    defaultValues.rooms && defaultValues.rooms.length !== 0
                      ? defaultValues.rooms[0]
                      : null;
                  let ages = [];
                  for (
                    let index = 0;
                    index < props.businessRules.travelers.options.length;
                    index++
                  ) {
                    const element = props.businessRules.travelers.options[index];
                    if (element.shouldDetailAges) {
                      ages.push(value.passengers.ages[index]);
                    } else {
                      ages.push([]);
                    }
                  }
                  if (val) {
                    setValue('rooms', [
                      { key: val.key, count: value.passengers.count, ages: ages }
                    ]);
                  }
                }
              }}
              enabledDoneButton={props.passengerSelector?.enabledBottomBtn ?? true}
            />
          ) : props.rooms && type !== 'car' && type !== 'activity' ? (
            <RoomsSelector
              key={travelClasses.defaultValue}
              origin={selectedOrigin}
              destination={selectedDestination}
              travelClasses={travelClasses}
              activateRouteOption={props.travelClasses.activateRouteOption}
              passengersRules={props.passengersRules}
              defaultValue={{
                rooms: defaultValues.rooms,
                travelClass: travelClasses.defaultValue
              }}
              rooms={props.rooms}
              onChange={(value) => {
                setValue('rooms', value.rooms);
                if (value.travelClass) {
                  setValue('travelClass', value.travelClass);
                }
              }}
              type={type}
              travelClassConfig={config.travelClassSelector}
              preventBottomDisplacement={props.preventBottomDisplacement}
            />
          ) : null}
          {config.dropOff?.enabled && type === 'car' ? (
            <Controller
              render={({ onChange }) => (
                <Listbox
                  label={translate('cmp-filters-drop-off', 'Drop-off')}
                  defaultValue={dropOff}
                  options={props.dropOff.options}
                  onChange={(newValue) => {
                    onChange(newValue);
                  }}
                />
              )}
              name="dropOff"
              control={control}
            />
          ) : null}

          {type.includes('car') && props.carTypes.options.length ? (
            <Controller
              render={({ onChange }) => (
                <ListSelector
                  defaultValue={carType}
                  selectedValue={carType}
                  fieldName="carType"
                  headerLabelKey="cmp-booking-car-type-label"
                  noSelectedItemLabelKey="cmp-car-type-label-not-selected-item"
                  doneButtonLabelKey="cmp-car-type-filter-done-button"
                  options={props.carTypes.options}
                  control={control}
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                />
              )}
              name="carType"
              control={control}
            />
          ) : null}
          {props.showAdvancedFilter && type !== 'activity' && (
            <AdvancedFilterSelector
              defaultPromoCode={props.promoCode}
              carTypes={props.carTypes}
              ages={props.ages}
              nationalities={props.nationalities}
              rules={rules}
              type={type}
              componentsConfigurations={config}
              control={control}
              register={register}
              setValue={setValue}
              getValues={getValues}
              hotelCategories={props.hotelCategories}
              enabledDoneButton={props.advancedSelector?.enabledBottomBtn ?? true}
              defaultExcludeBasicEconomy={props.excludeBasicEconomy}
            />
          )}
        </Inline>

        {type === 'car' && dropOff === 'same' ? (
          <Controller
            render={() => <React.Fragment></React.Fragment>}
            name="destination"
            control={control}
          />
        ) : (
          <React.Fragment></React.Fragment>
        )}

        <BookingFilters
          activeTab={props.activeTab}
          currentTab={props.currentTab}
          updateSelectedOrigin={setSelectedOrigin}
          updateSelectedDestination={setSelectedDestination}
          updateSelectedDepartureDate={setSelectedDepartureDate}
          updateSelectedReturnDate={setSelectedReturnDate}
          defaultOrigin={defaultValues.origin}
          selectedDepartureDate={selectedDepartureDate}
          selectedReturnDate={selectedReturnDate}
          defaultDestination={defaultValues.destination}
          defaultDates={props.defaultDates}
          fetchAirports={props.fetchAirports}
          filterFields={props.filterFields}
          onOriginChange={handleOriginChange}
          onAirlineChange={handleOnAirlineChange}
          onDestinationChange={handleDestinationChange}
          onDepartureDateChange={handleDepartureDateChange}
          onReturnDateChange={handleReturnDateChange}
          showOrigin={
            type !== 'hotel' && type !== 'activity' && (type !== 'car' || dropOff === 'different')
          }
          // showDestination={}
          showTimePickers={type === 'car'}
          componentsConfigurations={config}
          watch={watch}
          errors={errors}
          control={control}
          isLoading={loading}
          labels={props.labels}
          blackoutDates={props.blackoutDates}
          maxTripDuration={props.maxTripDuration}
          minTripDuration={props.minTripDuration}
          ctaHalfWidth={props.layout === 'dialog'}
          columns={columns}
          rows={rows}
          showAirlines={
            props.airlines?.enabled && type !== 'hotel' && type !== 'car' && type !== 'activity'
          }
          fetchAirlines={props.fetchAirlines}
          airlines={props.airlines}
          singleDateSelection={type.includes('flight') && flightType === 'ow'}
          prepopulatedAirlines={props.prepopulatedAirlines}
          originDestinationLogic={
            props.originDestinationLogic
              ? props.originDestinationLogic
              : OriginDestinationLogic.SAME
          }
          trigger={trigger}
          onSubmit={handleSubmit((data) => {
            props.onSubmit(data);
            handleInternalSubmit();
          }, handleErrors)}
          fetchBlackoutDates={blackoutDates}
          blackoutDatesIntegration={config?.blackoutDatesIntegration}
          flightType={flightType}
          setValue={setValue}
          getValues={getValues}
          disclaimer={props.disclaimer}
          allowHoverStyleInRange={props.allowHoverStyleInRange}
          timePickerOptions={props.businessRules.timePickers}
        />
      </Stack>
    </form>
  );
}
BookingPanel.defaultProps = {
  showAdvancedFilter: true
};
